import { useEffect } from 'react';
import { trackingApi } from '@/api/endpoints/tracking';
import { appConfig } from '@/config/app';
import { applyCrispTheme, applyCrispVisibility, isCrispChatEnabled, mountCrisp } from './crisp';

/**
 * Crisp монтируется один раз и только с разрешения рубильника админки
 * (`GET /crisp-config`, карточка docs/services/crisp.md): без `enabled: true`
 * скрипт не вставляется, и точки входа в чат уходят в письмо на support@.
 * Website id остаётся в сборке (`VITE_CRISP_WEBSITE_ID`); пустой — виджета нет
 * независимо от рубильника. Видимость лаунчера пересчитывается на resize (NAV-7).
 */
export function CrispChat() {
  useEffect(() => {
    const websiteId = appConfig.crispWebsiteId;
    if (!websiteId) return;
    let cancelled = false;
    let teardown: (() => void) | undefined;
    trackingApi
      .crispConfig()
      .then((response) => {
        if (cancelled || !isCrispChatEnabled(response)) return;
        teardown = mountAndWatch(websiteId);
      })
      .catch(() => {
        // Сеть или отказ ручки — чат остаётся выключенным: ошибка не может
        // поднять сторонний скрипт с куками у посетителя.
      });
    return () => {
      cancelled = true;
      teardown?.();
    };
  }, []);
  return null;
}

/** Вставить скрипт и держать лаунчер и цвет чата в согласии с окном и темой. */
function mountAndWatch(websiteId: string): () => void {
  mountCrisp(websiteId);
  applyCrispVisibility();
  // Скрипт async — первый push может быть no-op, страхуемся повторами.
  const t1 = window.setTimeout(applyCrispVisibility, 1000);
  const t2 = window.setTimeout(applyCrispVisibility, 3000);
  window.addEventListener('resize', applyCrispVisibility);
  // Переключение темы в приложении — чат перекрашивается следом.
  const themeWatch = new MutationObserver(applyCrispTheme);
  themeWatch.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });
  return () => {
    window.clearTimeout(t1);
    window.clearTimeout(t2);
    window.removeEventListener('resize', applyCrispVisibility);
    themeWatch.disconnect();
  };
}
